/* bolero-gallery-carousel.js — product-gallery carousel arrows.

   ECOM-59 (2026-09-12): extracted verbatim from the first inline <script> in
   snippets/product-gallery.liquid. That snippet renders more than once per PDP
   (main gallery + the quick-buy gallery in the recommendation rail), so the
   8.1 KB block was emitted twice into every product page's HTML.

   It contained no Liquid, and initAllGalleries() already selects
   ".product-gallery__carousel" document-wide and is idempotent per carousel
   (dataset.arrowsInitialized), so one copy per page initialises every gallery
   regardless of which snippet instance rendered it. Loaded once from
   layout/theme.liquid (defer). Logic unchanged.
*/
(function() {
  'use strict';
  
  function initGalleryCarousel(carousel) {
    // Check if already initialized
    if(carousel.dataset.arrowsInitialized === 'true') return;
    carousel.dataset.arrowsInitialized = 'true';
    
    // Get visible slides only (not hidden)
    const allSlides = carousel.querySelectorAll(".product-gallery__media");
    const visibleSlides = Array.from(allSlides).filter(slide => {
      return !slide.hasAttribute('hidden') && slide.offsetParent !== null;
    });
    
    if (visibleSlides.length <= 1) return;

    const wrapper = carousel.closest(".product-gallery__image-list");
    if (!wrapper) return;
    
    // Check if buttons already exist
    let prevBtn = wrapper.querySelector('.product-gallery-arrow--prev');
    let nextBtn = wrapper.querySelector('.product-gallery-arrow--next');
    
    if (!prevBtn || !nextBtn) {
      prevBtn = document.createElement("button");
      nextBtn = document.createElement("button");

      prevBtn.className = "product-gallery-arrow product-gallery-arrow--prev";
      nextBtn.className = "product-gallery-arrow product-gallery-arrow--next";

      prevBtn.setAttribute("aria-label", "Previous image");
      nextBtn.setAttribute("aria-label", "Next image");
      prevBtn.setAttribute("type", "button");

      prevBtn.innerHTML = `
        <svg aria-hidden="true" focusable="false" fill="none" width="16"
          viewBox="0 0 16 18">
          <path d="M11 1 3 9l8 8"
            stroke="currentColor"
            stroke-linecap="square"></path>
        </svg>
      `;

      nextBtn.innerHTML = `
        <svg aria-hidden="true" focusable="false" fill="none" width="16"
          viewBox="0 0 16 18">
          <path d="m5 17 8-8-8-8"
            stroke="currentColor"
            stroke-linecap="square"></path>
        </svg>
      `;

      wrapper.appendChild(prevBtn);
      wrapper.appendChild(nextBtn);
    }

    let slideWidth = 0;
    let isScrolling = false;
    
    const getSlideWidth = () => {
      if (visibleSlides.length === 0) return 0;
      const firstVisible = visibleSlides[0];
      const rect = firstVisible.getBoundingClientRect();
      const carouselRect = carousel.getBoundingClientRect();
      // Account for gap if any
      const gap = parseInt(getComputedStyle(carousel).gap) || 0;
      return rect.width + gap;
    };

    const updateSlideWidth = () => {
      slideWidth = getSlideWidth();
    };

    // Initial calculation
    updateSlideWidth();
    
    // Recalculate on resize with debounce
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateSlideWidth();
        toggleArrows();
      }, 100);
    });

    const goNext = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      updateSlideWidth();
      const currentScroll = carousel.scrollLeft;
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      
      // Find next visible slide
      let targetScroll = currentScroll + slideWidth;
      
      // Snap to nearest slide
      let nearestSlide = null;
      let minDistance = Infinity;
      
      visibleSlides.forEach(slide => {
        const slideLeft = slide.offsetLeft;
        const distance = Math.abs(slideLeft - targetScroll);
        if (distance < minDistance && slideLeft > currentScroll) {
          minDistance = distance;
          nearestSlide = slide;
        }
      });
      
      if (nearestSlide) {
        targetScroll = nearestSlide.offsetLeft;
      } else {
        // Fallback: scroll by slide width
        targetScroll = Math.min(currentScroll + slideWidth, maxScroll);
      }
      
      carousel.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
      
      setTimeout(() => {
        isScrolling = false;
        toggleArrows();
      }, 300);
    };

    const goPrev = () => {
      if (isScrolling) return;
      isScrolling = true;
      
      updateSlideWidth();
      const currentScroll = carousel.scrollLeft;
      
      // Find previous visible slide
      let targetScroll = currentScroll - slideWidth;
      let nearestSlide = null;
      let minDistance = Infinity;
      
      visibleSlides.forEach(slide => {
        const slideLeft = slide.offsetLeft;
        const distance = Math.abs(slideLeft - targetScroll);
        if (distance < minDistance && slideLeft < currentScroll) {
          minDistance = distance;
          nearestSlide = slide;
        }
      });
      
      if (nearestSlide) {
        targetScroll = nearestSlide.offsetLeft;
      } else {
        // Fallback: scroll by slide width
        targetScroll = Math.max(currentScroll - slideWidth, 0);
      }
      
      carousel.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
      
      setTimeout(() => {
        isScrolling = false;
        toggleArrows();
      }, 300);
    };

    // Add event listeners (remove old ones first if needed)
    const oldPrevClick = prevBtn.onclick;
    const oldNextClick = nextBtn.onclick;
    
    // Remove any existing listeners by cloning
    if (oldPrevClick || oldNextClick) {
      const newPrevBtn = prevBtn.cloneNode(true);
      const newNextBtn = nextBtn.cloneNode(true);
      prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
      nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
      prevBtn = newPrevBtn;
      nextBtn = newNextBtn;
    }
    
    prevBtn.addEventListener("click", goPrev);
    nextBtn.addEventListener("click", goNext);

    // Throttle scroll event
    let scrollTimeout;
    const toggleArrows = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = carousel.scrollLeft;
        const scrollWidth = carousel.scrollWidth;
        const clientWidth = carousel.clientWidth;
        const threshold = 5;
        
        // Check if carousel is scrollable
        const isScrollable = scrollWidth > clientWidth;
        
        if (!isScrollable) {
          prevBtn.style.display = "none";
          nextBtn.style.display = "none";
          prevBtn.disabled = true;
          nextBtn.disabled = true;
          return;
        }
        
        const isAtStart = scrollLeft <= threshold;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - threshold;
        
        // Update prev button
        if (isAtStart) {
          prevBtn.style.display = "none";
          prevBtn.disabled = true;
        } else {
          prevBtn.style.display = "flex";
          prevBtn.disabled = false;
        }
        
        // Update next button
        if (isAtEnd) {
          nextBtn.style.display = "none";
          nextBtn.disabled = true;
        } else {
          nextBtn.style.display = "flex";
          nextBtn.disabled = false;
        }
      }, 10);
    };

    carousel.addEventListener("scroll", toggleArrows, { passive: true });
    
    // Initial toggle
    toggleArrows();
    
    // Re-check when images load
    const images = carousel.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        toggleArrows();
      } else {
        img.addEventListener('load', toggleArrows, { once: true });
      }
    });
    
    // Update when variant changes (for filtered media)
    const observer = new MutationObserver(() => {
      updateSlideWidth();
      toggleArrows();
    });
    
    observer.observe(carousel, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
      subtree: true
    });
  }
  
  function initAllGalleries() {
    const carousels = document.querySelectorAll(".product-gallery__carousel");
    carousels.forEach(carousel => {
      initGalleryCarousel(carousel);
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }
  
  // Re-initialize on dynamic content load (for AJAX navigation)
  if (typeof window.Shopify !== 'undefined' && window.Shopify.theme) {
    document.addEventListener('shopify:section:load', initAllGalleries);
  }
  
  // Also initialize after a short delay to catch any late-loading content
  setTimeout(initAllGalleries, 500);
})();
